import type { PieLabelRenderProps } from 'recharts';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ViewChartConfig } from '../lib/schemas';
import { Skeleton } from './ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

export interface ViewRendererProps {
  type: string;
  chartConfig: ViewChartConfig;
  data: Record<string, unknown>[];
  isLoading?: boolean;
}

interface ChartProps {
  config: ViewChartConfig;
  data: Record<string, unknown>[];
}

const DEFAULT_COLORS = [
  '#4ade80',
  '#60a5fa',
  '#f59e0b',
  '#ef4444',
  '#a78bfa',
  '#34d399',
  '#fb923c',
  '#e879f9',
];

function getColor(index: number, override?: string): string {
  return override ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length] ?? '#4ade80';
}

function LineChartRenderer({ config, data }: ChartProps) {
  const yFields = config.y ?? [];
  return (
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#166534" />
        <XAxis dataKey={config.x ?? 'x'} stroke="#86efac" tick={{ fontSize: 11 }} />
        <YAxis stroke="#86efac" tick={{ fontSize: 11 }} />
        <Tooltip
          contentStyle={{ backgroundColor: '#14532d', border: '1px solid #166534', color: '#fff' }}
        />
        {yFields.map((y, i) => (
          <Line
            key={y.field}
            type="monotone"
            dataKey={y.field}
            name={y.label}
            stroke={getColor(i, y.color)}
            dot={false}
            strokeWidth={2}
          />
        ))}
        {config.show_trend_line && yFields[0] && (
          <ReferenceLine
            stroke="#facc15"
            strokeDasharray="6 3"
            label={{ value: 'Trend', fill: '#facc15', fontSize: 10 }}
          />
        )}
        {(config.annotations ?? []).map((a) => (
          <ReferenceLine key={a.x} x={a.x} stroke="#f87171" label={a.label} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function BarChartRenderer({ config, data }: ChartProps) {
  const yFields = config.y ?? [];
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#166534" />
        <XAxis dataKey={config.x ?? 'x'} stroke="#86efac" tick={{ fontSize: 11 }} />
        <YAxis stroke="#86efac" tick={{ fontSize: 11 }} />
        <Tooltip
          contentStyle={{ backgroundColor: '#14532d', border: '1px solid #166534', color: '#fff' }}
        />
        {yFields.map((y, i) => (
          <Bar key={y.field} dataKey={y.field} name={y.label} fill={getColor(i, y.color)} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function StackedBarRenderer({ config, data }: ChartProps) {
  const yFields = config.y ?? [];
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#166534" />
        <XAxis dataKey={config.x ?? 'x'} stroke="#86efac" tick={{ fontSize: 11 }} />
        <YAxis stroke="#86efac" tick={{ fontSize: 11 }} />
        <Tooltip
          contentStyle={{ backgroundColor: '#14532d', border: '1px solid #166534', color: '#fff' }}
        />
        {yFields.map((y, i) => (
          <Bar
            key={y.field}
            dataKey={y.field}
            name={y.label}
            fill={getColor(i, y.color)}
            stackId="stack"
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function PieChartRenderer({ config, data }: ChartProps) {
  const yField = config.y?.[0]?.field ?? 'value';
  const nameKey = config.x ?? 'name';
  return (
    <ResponsiveContainer width="100%" height={240}>
      <PieChart>
        <Pie
          data={data}
          dataKey={yField}
          nameKey={nameKey}
          cx="50%"
          cy="50%"
          outerRadius={90}
          label={(entry: PieLabelRenderProps) =>
            String((entry as unknown as Record<string, unknown>)[nameKey] ?? '')
          }
        >
          {data.map((row, i) => (
            <Cell key={String(row[nameKey] ?? i)} fill={getColor(i, config.y?.[i]?.color)} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{ backgroundColor: '#14532d', border: '1px solid #166534', color: '#fff' }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

function ScatterRenderer({ config, data }: ChartProps) {
  const xField = config.x ?? 'x';
  const yField = config.y?.[0]?.field ?? 'y';
  return (
    <ResponsiveContainer width="100%" height={240}>
      <ScatterChart>
        <CartesianGrid strokeDasharray="3 3" stroke="#166534" />
        <XAxis dataKey={xField} stroke="#86efac" tick={{ fontSize: 11 }} name={xField} />
        <YAxis dataKey={yField} stroke="#86efac" tick={{ fontSize: 11 }} name={yField} />
        <Tooltip
          contentStyle={{ backgroundColor: '#14532d', border: '1px solid #166534', color: '#fff' }}
          cursor={{ strokeDasharray: '3 3' }}
        />
        <Scatter data={data} fill={getColor(0, config.y?.[0]?.color)} />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

function GaugeRenderer({ config, data }: ChartProps) {
  const yField = config.y?.[0]?.field ?? 'value';
  const value = Number(data[0]?.[yField] ?? 0);
  const label = config.y?.[0]?.label ?? yField;
  // Simulate gauge with a half-donut pie
  const max = 100;
  const pct = Math.min(Math.max(value, 0), max);
  const gaugeData = [
    { value: pct, fill: '#4ade80' },
    { value: max - pct, fill: '#166534' },
  ];
  return (
    <div className="flex flex-col items-center">
      <ResponsiveContainer width="100%" height={160}>
        <PieChart>
          <Pie
            data={gaugeData}
            cx="50%"
            cy="80%"
            startAngle={180}
            endAngle={0}
            innerRadius={60}
            outerRadius={90}
            dataKey="value"
          >
            {gaugeData.map((entry) => (
              <Cell key={entry.fill} fill={entry.fill} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <p className="text-2xl font-bold -mt-8">{value}</p>
      <p className="text-sm text-ndd-400">{label}</p>
    </div>
  );
}

function CalendarRenderer({ config, data }: ChartProps) {
  const dateField = config.x ?? 'date';
  const valueField = config.y?.[0]?.field ?? 'value';
  if (data.length === 0) return <p className="text-sm text-ndd-500 p-4">No data</p>;

  // Group by week for a simple calendar grid
  const byDate = new Map<string, number>();
  for (const row of data) {
    const d = String(row[dateField] ?? '');
    const v = Number(row[valueField] ?? 0);
    byDate.set(d, v);
  }
  const dates = Array.from(byDate.keys()).sort();
  const maxVal = Math.max(...Array.from(byDate.values()));

  return (
    <div className="overflow-auto p-2">
      <div className="flex flex-wrap gap-1">
        {dates.map((d) => {
          const v = byDate.get(d) ?? 0;
          const intensity = maxVal > 0 ? v / maxVal : 0;
          const bg =
            intensity > 0.66
              ? 'bg-ndd-400'
              : intensity > 0.33
                ? 'bg-ndd-600'
                : intensity > 0
                  ? 'bg-ndd-800'
                  : 'bg-ndd-950';
          return <div key={d} className={`w-6 h-6 rounded-sm ${bg}`} title={`${d}: ${v}`} />;
        })}
      </div>
    </div>
  );
}

function TableRenderer({ config, data }: ChartProps) {
  if (data.length === 0) return <p className="text-sm text-ndd-500 p-4">No data</p>;
  const columns =
    config.y && config.y.length > 0
      ? config.y.map((y) => ({ key: y.field, label: y.label }))
      : Object.keys(data[0] ?? {}).map((k) => ({ key: k, label: k }));

  return (
    <div className="overflow-auto max-h-60">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHead key={col.key}>{col.label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row) => (
            <TableRow key={columns.map((c) => String(row[c.key] ?? '')).join('|')}>
              {columns.map((col) => (
                <TableCell key={col.key}>{String(row[col.key] ?? '')}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function KpiCardRenderer({ config, data }: ChartProps) {
  const yField = config.y?.[0]?.field ?? 'value';
  const label = config.y?.[0]?.label ?? yField;
  const value = data[0]?.[yField];
  const delta = data[0]?.delta;
  return (
    <div className="flex flex-col items-center justify-center py-6">
      <p className="text-sm text-ndd-400">{label}</p>
      <p className="text-5xl font-bold mt-2">{value != null ? String(value) : '—'}</p>
      {delta != null && (
        <p className={`text-sm mt-1 ${Number(delta) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {Number(delta) >= 0 ? '+' : ''}
          {String(delta)}
        </p>
      )}
    </div>
  );
}

function AlertListRenderer({ data }: ChartProps) {
  if (data.length === 0) return <p className="text-sm text-ndd-500 p-4">No alerts</p>;
  const SEVERITY_COLORS: Record<string, string> = {
    urgent: 'border-l-red-500 bg-red-950/30',
    warning: 'border-l-amber-500 bg-amber-950/30',
    info: 'border-l-blue-400 bg-blue-950/30',
  };
  return (
    <div className="space-y-2 p-2">
      {data.map((row) => {
        const severity = String(row.severity ?? 'info');
        return (
          <div
            key={`${String(row.subject_name ?? '')}-${String(row.message ?? '')}`}
            className={`rounded border-l-4 px-3 py-2 ${SEVERITY_COLORS[severity] ?? SEVERITY_COLORS.info}`}
          >
            <p className="text-sm font-medium">{String(row.subject_name ?? '')}</p>
            <p className="text-xs text-ndd-300">{String(row.message ?? '')}</p>
          </div>
        );
      })}
    </div>
  );
}

function MarkdownRenderer({ data }: ChartProps) {
  const content = String(data[0]?.content ?? '');
  return <div className="p-4 text-sm text-ndd-100 whitespace-pre-wrap font-mono">{content}</div>;
}

function HeatmapRenderer({ config, data }: ChartProps) {
  if (data.length === 0) return <p className="text-sm text-ndd-500 p-4">No data</p>;
  const xField = config.x ?? 'x';
  const yField = config.y?.[0]?.field ?? 'value';
  const values = data.map((r) => Number(r[yField] ?? 0));
  const maxVal = Math.max(...values, 1);

  return (
    <div className="overflow-auto p-2">
      <div className="flex flex-wrap gap-1">
        {data.map((row, i) => {
          const v = values[i] ?? 0;
          const intensity = v / maxVal;
          const bg =
            intensity > 0.66 ? 'bg-red-500' : intensity > 0.33 ? 'bg-amber-500' : 'bg-ndd-600';
          return (
            <div
              key={String(row[xField] ?? i)}
              className={`w-8 h-8 rounded-sm ${bg} flex items-center justify-center`}
              title={`${String(row[xField] ?? '')}: ${v}`}
            >
              <span className="text-xs text-white font-bold">{String(row[xField] ?? '')}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const RENDERERS: Record<string, React.ComponentType<ChartProps>> = {
  line_chart: LineChartRenderer,
  bar_chart: BarChartRenderer,
  stacked_bar: StackedBarRenderer,
  pie_chart: PieChartRenderer,
  scatter: ScatterRenderer,
  gauge: GaugeRenderer,
  calendar: CalendarRenderer,
  table: TableRenderer,
  kpi_card: KpiCardRenderer,
  alert_list: AlertListRenderer,
  markdown: MarkdownRenderer,
  heatmap: HeatmapRenderer,
};

export function ViewRenderer({ type, chartConfig, data, isLoading }: ViewRendererProps) {
  if (isLoading) {
    return <Skeleton className="w-full h-48" />;
  }

  const Renderer = RENDERERS[type];
  if (!Renderer) {
    return <p className="text-sm text-ndd-500 p-4">Unknown view type: {type}</p>;
  }

  return <Renderer config={chartConfig} data={data} />;
}
